import { getFullLanguageFromDefaultMapping } from './getMatchingLocale';
import { get } from 'lodash';
import { defaultMemoize } from 'reselect';

const createTranslationKeys = defaultMemoize((translationResource) => {
  if (translationResource) {
    let resourceKeys;
    Object.keys(translationResource).forEach(localeKey => {
      const translations = translationResource[localeKey];
      Object.keys(translations).forEach(key => {
        resourceKeys = {
          ...resourceKeys,
          [localeKey + '.' + key]: escapeCurlyBracesAndBackslashCharacters(translations[key])
        };
      });
    });
    return resourceKeys;
  }
  return {};
});

export function eventHasMultipleLanguages(event) {
  return get(event, ['eventFeatureSetup', 'website', 'multipleLanguages'], false) &&
  get(event, ['eventLocalesSetup', 'eventLocales'], []).length > 1;
}

/*
 * Determines if an event is single language and the event has a different default
 * culture code than the account's default culture code
 */
export function eventSingleLanguageDefaultNotAccountDefault(event, account) {
  const eventLocales = get(event, ['eventLocalesSetup', 'eventLocales'], []);
  const accountDefaultCultureCode = get(account, ['settings', 'defaultCultureCode'], '');
  return eventLocales.length === 1
    && accountDefaultCultureCode !== ''
    && eventLocales[0].cultureCode !== accountDefaultCultureCode;
}

export function customFieldShouldUseChoiceIds(event, account) {
  return eventHasMultipleLanguages(event) ||
    eventSingleLanguageDefaultNotAccountDefault(event, account);
}

/*
 * Function that returns whether the widget/field should use translations that are loaded into the translate object
 * or the translations from the userText object.
 */
export function checkIfWidgetShouldUseTranslations(event, localizedUserText, widgetId) {
  if (eventHasMultipleLanguages(event) && localizedUserText && localizedUserText.localizations) {
    return checkUserTextForWidget(localizedUserText, getFullLanguageFromDefaultMapping(event.cultureCode), widgetId);
  }
  return false;
}

/*
 * Function checks whether the userText translation for the current language is the same as the default translation.
 * If it is true then it means the widget/field should use the translation that has been loaded into the translate
 * object instead of the value from userText.
 */
export function checkUserTextForWidget(localizedUserText, defaultLocale, widgetId) {
  const localizations = localizedUserText.localizations;
  const currentLocale = localizedUserText.currentLocale;
  if (currentLocale && currentLocale !== defaultLocale) {
    if (localizations[currentLocale] && localizations[defaultLocale]) {
      const defaultTranslation = localizations[defaultLocale][`website.layoutItems.${widgetId}.config.displayName`];
      const currentTranslation = localizations[currentLocale][`website.layoutItems.${widgetId}.config.displayName`];
      return defaultTranslation === currentTranslation;
    }
    return true;
  }
  return false;
}

/*
 * Checks if multi language is enable is feature setup and the number of locales available for event are more then 1
 */
export function checkMultiLanguageEnableForEvent(event) {
  return (!get(event, ['eventFeatureSetup', 'website', 'multipleLanguages'], false));
}

export async function registerTranslationsFromSnapshot(resolver, translationSnapshotClient, defaultEventLocale,
  defaultAccountLocale, locale, plannerResolver, plannerLocale) {
  const promises = [];
  promises.push(getAccountResourceKeys(translationSnapshotClient,
    getFullLanguageFromDefaultMapping(defaultAccountLocale),
    getFullLanguageFromDefaultMapping(defaultEventLocale),
    getFullLanguageFromDefaultMapping(locale)));
  promises.push(getEventResourceKeys(translationSnapshotClient,
    getFullLanguageFromDefaultMapping(defaultEventLocale), getFullLanguageFromDefaultMapping(locale)));
  let resourceKeys;
  await Promise.all(promises).then(([accountResourceKeys, eventResourceKeys]) =>
    resourceKeys = {
      ...accountResourceKeys,
      ...eventResourceKeys
    }
  );
  resolver.registerTranslations(resourceKeys, resolver.context, locale);
  if (plannerResolver) {
    plannerResolver.registerTranslations(resourceKeys, plannerResolver.context, plannerLocale);
  }
}

/*
 * Function to get the account resource keys from account translation snapshots
 * It grabs the default account translations for default account locale
 * Then it grab account translations for default event locale and overwrites them
 * Then it grabs account translations for a selected locale if it doesn't match previous two
 */
async function getAccountResourceKeys(translationSnapshotClient, defaultAccountLocale, defaultEventLocale, locale) {
  const defaultAccountResourceKeys = await getAccountResourceKeysForLocale(translationSnapshotClient,
    defaultAccountLocale);
  let defaultEventResourceKeys = {};
  if (defaultAccountLocale !== defaultEventLocale) {
    defaultEventResourceKeys = await getAccountResourceKeysForLocale(translationSnapshotClient, defaultEventLocale);
  }
  if (locale !== defaultAccountLocale && locale !== defaultEventLocale) {
    const currentLocaleResourceKeys = await getAccountResourceKeysForLocale(translationSnapshotClient, locale);
    return {
      ...defaultAccountResourceKeys,
      ...defaultEventResourceKeys,
      ...currentLocaleResourceKeys
    };
  } else if (locale === defaultAccountLocale) {
    return {
      ...defaultEventResourceKeys,
      ...defaultAccountResourceKeys
    };
  }
  return {
    ...defaultAccountResourceKeys,
    ...defaultEventResourceKeys
  };
}

async function getAccountResourceKeysForLocale(translationSnapshotClient, locale) {
  try {
    const translationResources = await translationSnapshotClient.getAccountTranslations(locale);
    if (translationResources?.translationResource[locale]) {
      return createTranslationKeys(translationResources.translationResource[locale]);
    }
    return {};
  } catch {
  /*
   * TO DO:  FLEX-49320
   * Client call failed, most likely due to no resources for this locale so return an empty object
   * There is an upcoming story next PI for making languages active or inactive which should
   * help clean this up
   */
    return {};
  }
}

async function getEventResourceKeys(translationSnapshotClient, defaultEventLocale, locale) {
  const defaultEventResourceKeys = await getEventResourceKeysForLocale(translationSnapshotClient, defaultEventLocale);
  if (locale !== defaultEventLocale) {
    const translationResourceKeys = await getEventResourceKeysForLocale(translationSnapshotClient, locale);
    return { ...defaultEventResourceKeys, ...translationResourceKeys };
  }
  return defaultEventResourceKeys;
}

async function getEventResourceKeysForLocale(translationSnapshotClient, locale) {
  try {
    const translationResources = await translationSnapshotClient.getEventTranslations(locale);
    if (translationResources?.translationResource[locale]) {
      return createTranslationKeys(translationResources.translationResource[locale]);
    }
    return {};
  } catch {
  /*
   * TO-DO:  FLEX-49320
   * Client call failed, most likely due to no resources for this locale so return an empty object
   * There is an upcoming story next PI for making languages active or inactive which should
   * help clean this up
   */
    return {};
  }
}

export function convertLocaleToCorrectCasing(locale) {
  const localeParts = locale.split('-');
  if (localeParts.length === 2) {
    return `${localeParts[0].toLowerCase()}-${localeParts[1].toUpperCase()}`;
  }
  return locale.toLowerCase();
}

export function findMatchingLocaleFromAvailableLocales(incomingLocale, event) {
  const eventLocales = event.eventLocalesSetup.eventLocales;
  const localeParts = incomingLocale.split('-');
  let matchingLocale;
  if (localeParts.length === 2) {
    matchingLocale = eventLocales.find(locale => locale.cultureCode.toLowerCase() === incomingLocale.toLowerCase());
    if (!matchingLocale) {
      matchingLocale = eventLocales.find(locale =>
        locale.cultureCode.split('-')[0].toLowerCase() === localeParts[0].toLowerCase());
    }
  } else if (localeParts.length === 1) {
    matchingLocale = eventLocales.find(locale =>
      locale.cultureCode.split('-')[0].toLowerCase() === incomingLocale.toLowerCase());
  }
  return matchingLocale ? matchingLocale.cultureCode : event.cultureCode;
}

const reRegExpChar = /[\\{}]/g;

/**
 * Using this function we are escaping the JSX characters for translations that are
 * coming from the Normandy Text(Account and Event Translation Snapshots) We are
 * escaping the backslash character and the curly braces opening and closing.
 */
function escapeCurlyBracesAndBackslashCharacters(string) {
  return (string)
    ? string.replace(reRegExpChar, '\\$&')
    : (string || '');
}

/*
 * Used to default a missing language from the list of locales
 *  Feature Config doesn't allow for multiple languages from locales
 *  i.e. English defaults to en-US, so an en-GB event fails to load translations
 *  this defaults to first available language in locale list if event locale is missing
 */
function getLanguageForMissingCultureCode(locale, availableLocales) {
  if (availableLocales.filter(eventLocale => eventLocale.toLowerCase() === locale.toLowerCase()).length === 0) {
    const language = locale.indexOf('-') !== -1 ? locale.substring(0, locale.indexOf('-')) : locale.substring(0, 2);
    const localesContainingLanguage =
      availableLocales.filter(eventLocale => eventLocale.toLowerCase().startsWith(language.toLowerCase()));
    const missingLocaleDefault = localesContainingLanguage.length > 0 ? localesContainingLanguage[0] : locale;
    return convertLocaleToCorrectCasing(missingLocaleDefault);
  }
  return convertLocaleToCorrectCasing(locale);
}

function getAvailableLocalesForEvent(eventLocales) {
  return eventLocales.map(locale => locale.cultureCode.toLowerCase());
}

export function getEventLocales(event) {
  return getAvailableLocalesForEvent(get(event, ['eventLocalesSetup', 'eventLocales'], []));
}

export function getCurrentLocale(locale, locales) {
  return getLanguageForMissingCultureCode(locale, getAvailableLocalesForEvent(locales));
}

export function getEventDefaultCultureCode(event) {
  return getLanguageForMissingCultureCode(event.cultureCode, getEventLocales(event));
}

export function getMultiLanguageLocale(state) {
  const { multiLanguageLocale, event } = state;
  const { locale } = multiLanguageLocale;
  return getLanguageForMissingCultureCode(
    getFullLanguageFromDefaultMapping(locale), getEventLocales(event));
}
